import React, { useState } from "react";

function Installation() {
  const [activeTab, setActiveTab] = useState('installation');
  const [copiedCommand, setCopiedCommand] = useState('');

  const copyToClipboard = (text, commandId) => {
    navigator.clipboard.writeText(text);
    setCopiedCommand(commandId);
    setTimeout(() => setCopiedCommand(''), 2000);
  };

  const CodeBlock = ({ children, commandId, className = "" }) => (
    <div className={`bg-gray-900 text-green-400 p-4 rounded-lg font-mono text-sm relative ${className}`}>
      <pre className="whitespace-pre-wrap">{children}</pre>
      <button
        onClick={() => copyToClipboard(children, commandId)}
        className="absolute top-2 right-2 bg-[#6b7966] hover:bg-[#5d6a5b] text-white px-2 py-1 rounded text-xs"
      >
        {copiedCommand === commandId ? '✅ Copied!' : '📋 Copy'}
      </button>
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto p-6">
      {/* Header */}
      <div 
        className="text-white p-6 rounded-lg mb-6 shadow-lg"
        style={{
          background: 'linear-gradient(135deg, #354931 0%, #5d8156 50%, #86bb7c 100%)'
        }}
      >
        <h1 className="text-3xl font-bold mb-2">⚙️ PlasAnn Installation & Usage</h1>
        <p style={{ color: '#c8d3c5' }}>
          Complete guide to install and use PlasAnn for plasmid annotation
        </p>
      </div>

      {/* Tab Navigation */}
      <div className="flex space-x-4 mb-6">
        <button
          onClick={() => setActiveTab('installation')}
          className={`px-4 py-2 rounded-lg font-medium transition-colors ${
            activeTab === 'installation' 
              ? 'bg-[#6b7966] text-white' 
              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          }`}
        >
          📦 Installation
        </button>
        <button
          onClick={() => setActiveTab('usage')}
          className={`px-4 py-2 rounded-lg font-medium transition-colors ${
            activeTab === 'usage' 
              ? 'bg-[#6b7966] text-white' 
              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          }`}
        >
          🚀 Usage Examples
        </button>
        <button
          onClick={() => setActiveTab('arguments')}
          className={`px-4 py-2 rounded-lg font-medium transition-colors ${
            activeTab === 'arguments' 
              ? 'bg-[#6b7966] text-white' 
              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          }`}
        >
          📝 Arguments
        </button>
      </div>

      {/* Installation Tab */}
      {activeTab === 'installation' && (
        <div className="space-y-6">
          
          {/* Prerequisites */}
          <div className="bg-white shadow-lg rounded-lg p-6 border border-[#e1e8e1]">
            <h2 className="text-xl font-bold mb-4 text-[#4e5a4c]">📋 Prerequisites</h2>
            <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg mb-4">
              <h3 className="font-semibold text-yellow-800 mb-2">⚠️ Important: Install Dependencies First</h3>
              <p className="text-yellow-700 text-sm">
                PlasAnn requires external bioinformatics tools. Install these before installing PlasAnn:
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div className="bg-[#f1f4f1] p-4 rounded-lg text-center">
                <div className="text-2xl mb-2">🧬</div>
                <h3 className="font-semibold text-[#4e5a4c]">BLAST+</h3>
                <p className="text-sm text-gray-600">Sequence alignment</p>
              </div>
              <div className="bg-[#f1f4f1] p-4 rounded-lg text-center">
                <div className="text-2xl mb-2">🔍</div>
                <h3 className="font-semibold text-[#4e5a4c]">Prodigal</h3>
                <p className="text-sm text-gray-600">Gene prediction</p>
              </div>
              <div className="bg-[#f1f4f1] p-4 rounded-lg text-center">
                <div className="text-2xl mb-2">🧮</div>
                <h3 className="font-semibold text-[#4e5a4c]">Infernal</h3>
                <p className="text-sm text-gray-600">ncRNA detection</p>
              </div>
            </div>

            {/* Dependency Installation */}
            <h3 className="text-lg font-semibold mb-3 text-[#6b7966]">Install Dependencies</h3>
            
            <div className="space-y-4">
              <div>
                <h4 className="font-medium text-gray-700 mb-2">🐍 Using Conda (Recommended)</h4>
                <CodeBlock commandId="conda-deps">conda install -c bioconda blast prodigal infernal</CodeBlock>
              </div>
              
              <div>
                <h4 className="font-medium text-gray-700 mb-2">🍺 macOS with Homebrew</h4>
                <CodeBlock commandId="brew-deps">brew install blast prodigal infernal</CodeBlock>
              </div>
              
              <div>
                <h4 className="font-medium text-gray-700 mb-2">🐧 Ubuntu/Debian</h4>
                <CodeBlock commandId="apt-deps">sudo apt update
sudo apt install ncbi-blast+ prodigal infernal</CodeBlock>
              </div>
            </div>
          </div>

          {/* PlasAnn Installation */}
          <div className="bg-white shadow-lg rounded-lg p-6 border border-[#e1e8e1]">
            <h2 className="text-xl font-bold mb-4 text-[#4e5a4c]">🚀 PlasAnn Installation</h2>
            
            <div className="space-y-6">
              
              {/* Method 1: pip */}
              <div>
                <h3 className="text-lg font-semibold mb-3 text-[#6b7966] flex items-center">
                  <span className="mr-2">📦</span>
                  Method 1: Install with pip
                </h3>
                <p className="text-gray-600 mb-3">Install the latest stable version from PyPI:</p>
                <CodeBlock commandId="pip-install">pip install plasann</CodeBlock>
              </div>

              {/* Method 2: conda */}
              <div>
                <h3 className="text-lg font-semibold mb-3 text-[#6b7966] flex items-center">
                  <span className="mr-2">🐍</span>
                  Method 2: Install with conda
                </h3>
                <p className="text-gray-600 mb-3">Install from bioconda channel:</p>
                <CodeBlock commandId="conda-install">conda install -c bioconda plasann</CodeBlock>
              </div>

              {/* Method 3: From source */}
              <div>
                <h3 className="text-lg font-semibold mb-3 text-[#6b7966] flex items-center">
                  <span className="mr-2">💻</span>
                  Method 3: Install from source
                </h3>
                <p className="text-gray-600 mb-3">For development or latest features:</p>
                <CodeBlock commandId="source-install">git clone https://github.com/ajlopakin/PlasAnn.git
cd PlasAnn
pip install -e .</CodeBlock>
              </div>
            </div>
          </div>

          {/* Verification */}
          <div className="bg-white shadow-lg rounded-lg p-6 border border-[#e1e8e1]">
            <h2 className="text-xl font-bold mb-4 text-[#4e5a4c]">✅ Verify Installation</h2>
            
            <div className="space-y-4">
              <div>
                <h3 className="font-medium text-gray-700 mb-2">Check PlasAnn version:</h3>
                <CodeBlock commandId="version-check">PlasAnn --version</CodeBlock>
              </div>
              
              <div>
                <h3 className="font-medium text-gray-700 mb-2">Check all dependencies:</h3>
                <CodeBlock commandId="deps-check">PlasAnn --check-deps</CodeBlock>
              </div>
            </div>

            <div className="mt-4 bg-green-50 border border-green-200 p-4 rounded-lg">
              <h3 className="font-semibold text-green-800 mb-2">✅ Success!</h3>
              <p className="text-green-700 text-sm">
                If all dependencies show "Found", you're ready to use PlasAnn!
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Usage Tab */}
      {activeTab === 'usage' && (
        <div className="space-y-6">
          
          {/* Basic Usage */}
          <div className="bg-white shadow-lg rounded-lg p-6 border border-[#e1e8e1]">
            <h2 className="text-xl font-bold mb-4 text-[#4e5a4c]">🚀 Basic Usage</h2>
            
            <div className="space-y-6">
              
              <div>
                <h3 className="text-lg font-semibold mb-3 text-[#6b7966]">Single FASTA File</h3>
                <p className="text-gray-600 mb-3">Annotate a single plasmid FASTA file:</p>
                <CodeBlock commandId="basic-fasta">PlasAnn -i plasmid.fasta -o results -t fasta</CodeBlock>
              </div>

              <div>
                <h3 className="text-lg font-semibold mb-3 text-[#6b7966]">Single GenBank File</h3>
                <p className="text-gray-600 mb-3">Process a GenBank file (retains existing annotations):</p>
                <CodeBlock commandId="basic-genbank">PlasAnn -i plasmid.gb -o results -t genbank</CodeBlock>
              </div>

              <div>
                <h3 className="text-lg font-semibold mb-3 text-[#6b7966]">Auto-Detection</h3>
                <p className="text-gray-600 mb-3">Process mixed FASTA and GenBank files in a folder:</p>
                <CodeBlock commandId="auto-detect">PlasAnn -i mixed_folder/ -o results -t auto</CodeBlock>
              </div>

              <div>
                <h3 className="text-lg font-semibold mb-3 text-[#6b7966]">Custom Output Name</h3>
                <p className="text-gray-600 mb-3">Specify a custom name for the output:</p>
                <CodeBlock commandId="custom-name">PlasAnn -i plasmid.fasta -o results -t fasta -n my_plasmid</CodeBlock>
              </div>
            </div>
          </div>

          {/* Advanced Usage */}
          <div className="bg-white shadow-lg rounded-lg p-6 border border-[#e1e8e1]">
            <h2 className="text-xl font-bold mb-4 text-[#4e5a4c]">🔬 Advanced Usage</h2>
            
            <div className="space-y-6">
              
              <div>
                <h3 className="text-lg font-semibold mb-3 text-[#6b7966]">Enhanced UniProt Annotation</h3>
                <p className="text-gray-600 mb-3">Run comprehensive protein annotation (slower but thorough):</p>
                <CodeBlock commandId="uniprot-blast">PlasAnn -i plasmid.fasta -o results -t fasta --uniprot-blast</CodeBlock>
                <div className="bg-yellow-50 border border-yellow-200 p-3 rounded mt-2">
                  <p className="text-yellow-700 text-sm">⚠️ UniProt BLAST significantly increases runtime.</p>
                </div>
              </div>

              <div>
                <h3 className="text-lg font-semibold mb-3 text-[#6b7966]">GenBank Processing Modes</h3>
                <div className="space-y-3">
                  <div>
                    <h4 className="font-medium text-gray-700">Retain original annotations (default):</h4>
                    <CodeBlock commandId="genbank-retain">PlasAnn -i plasmid.gb -o results -t genbank --retain</CodeBlock>
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-700">Re-annotate with Prodigal:</h4>
                    <CodeBlock commandId="genbank-overwrite">PlasAnn -i plasmid.gb -o results -t genbank --overwrite</CodeBlock>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-lg font-semibold mb-3 text-[#6b7966]">Custom UniProt Settings</h3>
                <p className="text-gray-600 mb-3">Customize UniProt BLAST parameters:</p>
                <CodeBlock commandId="custom-uniprot">PlasAnn -i plasmid.fasta -o results -t fasta --uniprot-blast --min-identity 70 --uniprot-tsv custom_uniprot.tsv</CodeBlock>
              </div>
            </div>
          </div>

          {/* Batch Processing */}
          <div className="bg-white shadow-lg rounded-lg p-6 border border-[#e1e8e1]">
            <h2 className="text-xl font-bold mb-4 text-[#4e5a4c]">📁 Batch Processing</h2>
            
            <div className="space-y-6">
              
              <div>
                <h3 className="text-lg font-semibold mb-3 text-[#6b7966]">Process Multiple FASTA Files</h3>
                <CodeBlock commandId="batch-fasta">PlasAnn -i fasta_folder/ -o results -t fasta</CodeBlock>
              </div>

              <div>
                <h3 className="text-lg font-semibold mb-3 text-[#6b7966]">Process Multiple GenBank Files</h3>
                <CodeBlock commandId="batch-genbank">PlasAnn -i genbank_folder/ -o results -t genbank</CodeBlock>
              </div>

              <div>
                <h3 className="text-lg font-semibold mb-3 text-[#6b7966]">Batch with UniProt Enhancement</h3>
                <CodeBlock commandId="batch-uniprot">PlasAnn -i mixed_folder/ -o results -t auto --uniprot-blast</CodeBlock>
                <div className="bg-orange-50 border border-orange-200 p-3 rounded mt-2">
                  <p className="text-orange-700 text-sm">⏱️ Batch processing with UniProt can take several hours depending on file count.</p>
                </div>
              </div>
            </div>
          </div>

          {/* Output Structure */}
          <div className="bg-white shadow-lg rounded-lg p-6 border border-[#e1e8e1]">
            <h2 className="text-xl font-bold mb-4 text-[#4e5a4c]">📊 Output Files</h2>
            <p className="text-gray-600 mb-4">PlasAnn generates the following output files for each processed plasmid:</p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-[#f1f4f1] p-4 rounded-lg">
                <div className="flex items-center mb-2">
                  <span className="text-xl mr-2">📊</span>
                  <h3 className="font-semibold text-[#4e5a4c]">CSV Annotations</h3>
                </div>
                <p className="text-sm text-gray-600">Detailed feature annotations in tabular format</p>
                <code className="text-xs bg-gray-200 px-2 py-1 rounded mt-2 block">plasmid_annotations.csv</code>
              </div>
              
              <div className="bg-[#f1f4f1] p-4 rounded-lg">
                <div className="flex items-center mb-2">
                  <span className="text-xl mr-2">📄</span>
                  <h3 className="font-semibold text-[#4e5a4c]">GenBank File</h3>
                </div>
                <p className="text-sm text-gray-600">Standardized format for other tools</p>
                <code className="text-xs bg-gray-200 px-2 py-1 rounded mt-2 block">plasmid_genbank.gbk</code>
              </div>
              
              <div className="bg-[#f1f4f1] p-4 rounded-lg">
                <div className="flex items-center mb-2">
                  <span className="text-xl mr-2">🎨</span>
                  <h3 className="font-semibold text-[#4e5a4c]">Plasmid Map</h3>
                </div>
                <p className="text-sm text-gray-600">Circular visualization of annotations</p>
                <code className="text-xs bg-gray-200 px-2 py-1 rounded mt-2 block">plasmid_map.png</code>
              </div>
              
              <div className="bg-[#f1f4f1] p-4 rounded-lg">
                <div className="flex items-center mb-2">
                  <span className="text-xl mr-2">🧬</span>
                  <h3 className="font-semibold text-[#4e5a4c]">UniProt Enhanced</h3>
                </div>
                <p className="text-sm text-gray-600">Additional CSV with UniProt data (if enabled)</p>
                <code className="text-xs bg-gray-200 px-2 py-1 rounded mt-2 block">plasmid_annotations_with_uniprot.csv</code>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Arguments Tab */}
      {activeTab === 'arguments' && (
        <div className="space-y-6">
          
          {/* Required Arguments */}
          <div className="bg-white shadow-lg rounded-lg p-6 border border-[#e1e8e1]">
            <h2 className="text-xl font-bold mb-4 text-[#4e5a4c]">📝 Required Arguments</h2>
            
            <div className="space-y-4">
              <div className="border-l-4 border-red-400 pl-4">
                <h3 className="font-semibold text-gray-800">-i, --input</h3>
                <p className="text-gray-600">Input FASTA or GenBank file/folder</p>
                <code className="text-sm bg-gray-100 px-2 py-1 rounded">PlasAnn -i plasmid.fasta</code>
              </div>
              
              <div className="border-l-4 border-red-400 pl-4">
                <h3 className="font-semibold text-gray-800">-o, --output</h3>
                <p className="text-gray-600">Output directory for results</p>
                <code className="text-sm bg-gray-100 px-2 py-1 rounded">PlasAnn -o results/</code>
              </div>
              
              <div className="border-l-4 border-red-400 pl-4">
                <h3 className="font-semibold text-gray-800">-t, --type</h3>
                <p className="text-gray-600">Input type: fasta, genbank, or auto</p>
                <code className="text-sm bg-gray-100 px-2 py-1 rounded">PlasAnn -t fasta</code>
              </div>
            </div>
          </div>

          {/* Optional Arguments */}
          <div className="bg-white shadow-lg rounded-lg p-6 border border-[#e1e8e1]">
            <h2 className="text-xl font-bold mb-4 text-[#4e5a4c]">🔧 Optional Arguments</h2>
            
            <div className="space-y-4">
              <div className="border-l-4 border-blue-400 pl-4">
                <h3 className="font-semibold text-gray-800">-n, --name</h3>
                <p className="text-gray-600">Custom name for output subfolder (single files only)</p>
                <code className="text-sm bg-gray-100 px-2 py-1 rounded">PlasAnn -n my_plasmid</code>
              </div>
              
              <div className="border-l-4 border-blue-400 pl-4">
                <h3 className="font-semibold text-gray-800">--overwrite</h3>
                <p className="text-gray-600">Use Prodigal on GenBank sequence (ignore existing annotations)</p>
                <code className="text-sm bg-gray-100 px-2 py-1 rounded">PlasAnn --overwrite</code>
              </div>
              
              <div className="border-l-4 border-blue-400 pl-4">
                <h3 className="font-semibold text-gray-800">--retain</h3>
                <p className="text-gray-600">Use GenBank annotations with fallback to translation (default)</p>
                <code className="text-sm bg-gray-100 px-2 py-1 rounded">PlasAnn --retain</code>
              </div>
            </div>
          </div>

          {/* UniProt Arguments */}
          <div className="bg-white shadow-lg rounded-lg p-6 border border-[#e1e8e1]">
            <h2 className="text-xl font-bold mb-4 text-[#4e5a4c]">🧬 UniProt BLAST Arguments</h2>
            
            <div className="space-y-4">
              <div className="border-l-4 border-green-400 pl-4">
                <h3 className="font-semibold text-gray-800">--uniprot-blast</h3>
                <p className="text-gray-600">Run optional UniProt BLAST annotation (slow but comprehensive)</p>
                <code className="text-sm bg-gray-100 px-2 py-1 rounded">PlasAnn --uniprot-blast</code>
              </div>
              
              <div className="border-l-4 border-green-400 pl-4">
                <h3 className="font-semibold text-gray-800">--uniprot-tsv</h3>
                <p className="text-gray-600">Path to UniProt TSV file (default: Database/uniprot_plasmids.tsv)</p>
                <code className="text-sm bg-gray-100 px-2 py-1 rounded">PlasAnn --uniprot-tsv custom.tsv</code>
              </div>
              
              <div className="border-l-4 border-green-400 pl-4">
                <h3 className="font-semibold text-gray-800">--min-identity</h3>
                <p className="text-gray-600">Minimum identity percentage for UniProt BLAST hits (default: 50%)</p>
                <code className="text-sm bg-gray-100 px-2 py-1 rounded">PlasAnn --min-identity 70</code>
              </div>
            </div>
          </div>

          {/* System Arguments */}
          <div className="bg-white shadow-lg rounded-lg p-6 border border-[#e1e8e1]">
            <h2 className="text-xl font-bold mb-4 text-[#4e5a4c]">🔍 System Arguments</h2>
            
            <div className="space-y-4">
              <div className="border-l-4 border-purple-400 pl-4">
                <h3 className="font-semibold text-gray-800">--version</h3>
                <p className="text-gray-600">Show PlasAnn version and exit</p>
                <code className="text-sm bg-gray-100 px-2 py-1 rounded">PlasAnn --version</code>
              </div>
              
              <div className="border-l-4 border-purple-400 pl-4">
                <h3 className="font-semibold text-gray-800">--check-deps</h3>
                <p className="text-gray-600">Check external dependency status and exit</p>
                <code className="text-sm bg-gray-100 px-2 py-1 rounded">PlasAnn --check-deps</code>
              </div>
            </div>
          </div>

          {/* Help */}
          <div className="bg-[#f8faf8] border border-[#d1ddd1] rounded-lg p-4">
            <h3 className="font-semibold text-[#4e5a4c] mb-2">❓ Need Help?</h3>
            <p className="text-sm text-gray-600 mb-2">
              Get comprehensive help and usage information:
            </p>
            <CodeBlock commandId="help">PlasAnn -h</CodeBlock>
          </div>
        </div>
      )}
    </div>
  );
}

export default Installation;